/**
 * Catalogo.
 *
 * POR QUE LA URL DICE `-interno`
 * ------------------------------
 * `/catalogo` a secas ya esta ocupada por la tienda online: ese es el link que
 * se le manda al cliente por WhatsApp y no se puede mover sin romperselo a
 * todos. La pantalla se llama «Catalogo» igual; lo que tiene apellido es la
 * direccion.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Que vendo, a cuanto, y de donde sale ese precio."
 *
 * LAS SECCIONES SON LAS DEL NEGOCIO
 * ---------------------------------
 * iPhone · Usados · Accesorios · Perfumes · Vapers · Otros — el mismo mapa
 * mental con el que se atiende el mostrador, no el arbol de rubros de la base.
 *
 * UNA SOLA LISTA PARA LAS NUEVE SECCIONES
 * ---------------------------------------
 * Cambiar de solapa cambia QUE se lista, no COMO se lista. El mismo buscador,
 * los mismos filtros, las mismas columnas y la misma densidad en las nueve.
 *
 * Durante un tiempo no fue asi: iPhone y Usados tenian cada uno su grilla por
 * modelo, linda de mirar y pobre de usar — sin buscador, sin filtros, sin
 * costo, sin stock, sin el pie que avisa cuando la lista se recorto. Eran
 * tres pantallas adentro de una, y las dos distintas eran justo las de los
 * productos que mas se tocan. Hoy son una.
 *
 * De aquellas vistas queda lo que si valia:
 *
 *  - iPhone: el ORDEN del lineup (`ordenarIphones`), modelos nuevos arriba y
 *    capacidades de menor a mayor. Es como se recita en el mostrador: "¿el 17
 *    Pro? ¿en que memoria?".
 *  - Usados: la seña de cada unidad (capacidad, color, bateria, IMEI), sus
 *    chips —en taller, vendido, sin precio, falta IMEI, referencia— y el boton
 *    de «ya lo tengo». Entran por `extras`, sin que la lista sepa de usados.
 *    El orden es el del mostrador (`ordenarUsados`): lo que tengo, lo que
 *    consigo, lo que ya se fue.
 *  - Accesorios / Perfumes / Vapers: el catalogo comun, ya filtrado a los
 *    rubros que componen cada seccion.
 *  - Otros: todo lo que no cayo arriba (Samsung, Xiaomi, consolas, el resto
 *    de Apple...). El cajon existe para que nada quede invisible.
 *
 * LO QUE ESTA PANTALLA NO HACE
 * ----------------------------
 * No mueve stock a mano. `stock_actual` lo mantienen los ingresos y las
 * salidas; el alta de un usado genera su propio ingreso de una unidad, y el
 * resto se corrige en /inventario o contando en /relevamiento.
 *
 * Lo que si ofrece, desde la ficha, es CORREGIR CONTANDO: se pide cuantas hay y
 * el ajuste por la diferencia lo arma el sistema. Sigue siendo un movimiento con
 * su rastro — no es editar el numero — pero se pregunta en el idioma del
 * estante, que durante la puesta a punto del catalogo es la unica operacion de
 * stock que se hace cien veces.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Download, Package, Plus } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { Segmented } from "@/components/ui/segmented";
import { DialogAlta } from "@/components/alta/DialogAlta";
import { type FormAlta, type TipoAlta } from "@/components/alta/logica";
import { mostrarCantidad } from "@/components/catalogo/Campos";
import { DialogExportar } from "@/components/catalogo/DialogExportar";
import { DialogBorrarProducto } from "@/components/catalogo/DialogBorrarProducto";
import {
  RUBROS_POR_SECCION,
  SECCIONES,
  conteosDeSecciones,
  esUsadoIphone,
  mapaRubroASeccion,
  mostrarLineup,
  seccionDe as seccionDeProducto,
  type Seccion,
} from "@/components/catalogo/secciones";
import { DialogProducto } from "@/components/catalogo/DialogProducto";
import { DialogConteo, type MovimientoDeConteo } from "@/components/catalogo/DialogsMovimiento";
import type { UnidadesDeColor } from "@/components/catalogo/RepartoPorColor";
import { useIngresoRepartido } from "@/components/catalogo/repartir";
import { DialogMovimientos } from "@/components/catalogo/MovimientosStock";
import { ListaProductos } from "@/components/catalogo/ListaProductos";
import {
  FILTROS_INICIALES,
  filtrarPorTexto,
  filtrosParaBackend,
  type FiltrosUi,
} from "@/components/catalogo/logica";
import {
  claveMemoriaOculta,
  claveOculta,
  extrasDeIphones,
  gruposDeModelos,
  memoriasOcultasDe,
  modelosOcultosDe,
  pendientesDeIphone,
  type SeccionDeModelos,
} from "@/components/iphones/extras";
import {
  cambiosDeMemoria,
  fichaDeMemoria,
  type PreciosDeMemoria,
  type RenglonDeMemoria,
} from "@/components/iphones/renglones";
import { agruparEnModelos, ordenarIphones, stockConUnidades } from "@/components/iphones/logica";
import { useCrearGuiado } from "@/components/alta/crear";
import { DialogUsado } from "@/components/usados/DialogUsado";
import { extrasDeUsados } from "@/components/usados/extras";
import {
  aCambiosUsado,
  esReferencia,
  estaVendido,
  ordenarUsados,
  pendientesUsado,
  type FormUsado,
} from "@/components/usados/logica";
import { indexarEnTaller } from "@/components/taller/logica";
import {
  useActualizarConfig,
  useActualizarProducto,
  useConfig,
  useCrearProducto,
  useEliminarProducto,
  useIngresoStock,
  useProductos,
  useProveedores,
  useRubros,
  useSalidaStock,
  useUsadosEnTaller,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { TOPE_FILAS } from "@/lib/usar-ver-mas";
import {
  aNumero,
  type IngresoStock,
  type Producto,
  type ResultadoMovimientoStock,
} from "@/lib/tipos";

export const Route = createFileRoute("/catalogo-interno")({
  component: Catalogo,
  // El buscador global enlaza a un producto puntual con `?id=`. Se valida en vez
  // de confiar: la URL la puede tipear cualquiera.
  validateSearch: (busqueda: Record<string, unknown>): { id?: number } => {
    const id = Number(busqueda.id);
    return Number.isInteger(id) && id > 0 ? { id } : {};
  },
});

/**
 * Cuantos productos se traen de una. Es el TOPE del sistema (`TOPE_FILAS`), no
 * un numero elegido: el backend no devuelve mas de 500 aunque se le pidan.
 *
 * Hoy el catalogo entra holgado (~400). El dia que no entre, esta pantalla no
 * puede limitarse a mostrar menos filas: las secciones, los contadores de las
 * solapas y los desplegables de familia, marca y proveedor se calculan en el
 * navegador sobre lo que entro en esta ventana, asi que se quedarian cortos sin
 * decirlo. Por eso el pie de la lista avisa explicitamente, y por eso mover
 * esos calculos a la base esta anotado en `docs/pendientes.md`.
 */
const TOPE = TOPE_FILAS;

/**
 * Manzana Mordida es un negocio de Apple: por mas que venda perfumes y vapes,
 * lo principal va primero y con nombre propio. Las secciones espejan las
 * planillas del Drive que este sistema reemplaza:
 *
 *   Sellados     <- APPLE ORIGINAL (los iPhone nuevos, los principales)
 *   Usados       <- STOCK USADOS + las secciones de usados de las planillas
 *   Apple        <- APPLE ORIGINAL (el resto del ecosistema: Mac, iPad,
 *                   Watch, AirPods, cargadores originales)
 *   Accesorios   <- ACCESORIOS MULTIMARCAS, SIMIL APPLE/STANLEY, JBL, SOUNDPEATS
 *   Perfumes     <- INTERNATIONAL FRAGRANCES
 *   Vapers       <- CALIFORNIAN VAPES
 *   Otras marcas  <- CELULARES (nuevos y usados de otras marcas)
 *   Otros equipos <- TABLETS, SMARTWATCH, CONSOLAS Y CAMARAS
 *
 * Desde 0044 las secciones se arman por FAMILIA y nunca por marca: «Samsung» y
 * «Xiaomi» dejaron de ser un lugar y pasaron a ser un valor de `marca`, que
 * cruza todas las secciones. «Otras marcas» volvio el 29/09/2026 pero sigue
 * siendo una familia —«Celulares»—: es el nombre con el que se la busca en el
 * local, no un filtro por marca.
 */
const VISTAS = SECCIONES;

type Vista = Seccion;

/**
 * El botón de alta sabe dónde estás: en Perfumes se carga un perfume, sin
 * pasar por el selector. Y el selector —al que se llega con «Cambiar»— ofrece
 * solo lo que va en la sección: parado en «Otras marcas» no se carga un iPhone
 * sellado. Lo que no entra en ninguna opción sale por el formulario completo.
 */
interface AltaDeVista {
  label: string;
  tipo: TipoAlta | null;
  tipos: ReadonlyArray<TipoAlta>;
}

const ALTA_POR_VISTA: Record<Vista, AltaDeVista> = {
  iphone: { label: "Cargar iPhone", tipo: "iphone", tipos: ["iphone"] },
  apple: { label: "Cargar producto Apple", tipo: "equipo", tipos: ["equipo"] },
  // va directo al asistente de usados
  usados: { label: "Cargar usado", tipo: null, tipos: [] },
  marcas: { label: "Cargar celular", tipo: "celular", tipos: ["celular"] },
  accesorios: {
    label: "Cargar accesorio",
    tipo: "accesorio",
    tipos: ["accesorio"],
  },
  perfumes: { label: "Cargar perfume", tipo: "perfume", tipos: ["perfume"] },
  vapers: { label: "Cargar vaper", tipo: "vaper", tipos: ["vaper"] },
  otros: { label: "Cargar equipo", tipo: "equipo", tipos: ["equipo"] },
  apedido: { label: "Cargar a pedido", tipo: "apedido", tipos: ["apedido"] },
};

/**
 * Que dice una seccion cuando esta vacia DE VERDAD, sin ningun filtro puesto.
 *
 * "No hay productos con estos filtros" no sirve si no hay filtros: lo que
 * falta es el primer producto, y cada seccion sabe con que boton se carga —el
 * mismo que esta arriba a la derecha, con ese nombre.
 */
/**
 * Por que vale la pena completar lo que falta, en la seccion donde falta.
 *
 * El cartel no dice "hay datos incompletos" y ya: dice que se pierde por no
 * tenerlos. Un aviso que no explica la consecuencia se apaga solo a la semana.
 */
const PORQUE_IMPORTA: Partial<Record<Vista, string>> = {
  usados: "Sin IMEI no se puede probar cuál era; sin precio no se puede vender.",
  iphone:
    "Un equipo en el local sin color no se puede ofrecer por teléfono, y un modelo sin lineup deja la pregunta «¿en qué colores viene?» sin respuesta.",
};

const VACIO_POR_VISTA: Record<Vista, string> = {
  iphone:
    "Todavía no hay iPhones sellados cargados. Poné el precio de una memoria en el renglón del modelo, y las unidades entran desde Inventario, con «Ingreso».",
  apple: "Todavía no hay productos Apple cargados. El primero entra con «Cargar producto Apple».",
  usados:
    "Todavía no hay equipos usados cargados. Entran desde Inventario (Ingreso, «iPhone usado»), o solos, cuando se recibe uno en parte de pago.",
  marcas:
    "Todavía no hay celulares de otras marcas. El primero entra con «Cargar celular», o solo, cuando se reciba uno en parte de pago.",
  accesorios: "Todavía no hay accesorios cargados. El primero entra con «Cargar accesorio».",
  perfumes: "Todavía no hay perfumes cargados. El primero entra con «Cargar perfume».",
  vapers: "Todavía no hay vapers cargados. El primero entra con «Cargar vaper».",
  otros: "Todavía no hay equipos de otras familias. El primero entra con «Cargar equipo».",
  apedido: "Todavía no hay nada a pedido. El primero entra con «Cargar a pedido».",
};

/**
 * Con que arranca el alta cuando se abre desde la solapa "A pedido": con la
 * casilla marcada. El rubro no: lo a pedido vive en la familia de la cosa
 * —una MacBook en Apple— y lo elige quien carga. Hasta el 29/09/2026
 * caia en un rubro «A pedido» sin publicar, y no salia en la tienda (0099).
 *
 * Constante a proposito: si fuera un objeto nuevo en cada render, el efecto
 * que siembra el formulario se volveria a disparar y borraria lo tipeado.
 */
const PRESET_A_PEDIDO = { aPedido: true };

function Catalogo(): JSX.Element {
  const navegar = useNavigate();
  const { id: idEnlazado } = Route.useSearch();

  const [vista, setVista] = useState<Vista>("iphone");
  /**
   * Las dos secciones de equipos: Sellados y Usados.
   *
   * Un iPhone no se da de alta, ENTRA (ver el Ingreso de Inventario: «¿Qué
   * entró?»): el lineup ya está en la lista y lo que falta no es un producto
   * nuevo sino las unidades que llegaron. Por eso estas dos secciones no
   * llevan el botón de alta del encabezado ni el de Relevamiento —que es del
   * catálogo de mostrador, no de los equipos—, y se quedan solo con Exportar.
   */
  const seccionDeEquipos = vista === "iphone" || vista === "usados";
  const [filtros, setFiltros] = useState<FiltrosUi>(FILTROS_INICIALES);
  /**
   * «Ver solo los que les falta algo». No vive en `FiltrosUi` porque no es un
   * filtro del catalogo sino de una sola seccion: Usados es la unica donde
   * cada fila es una ficha que alguien tiene que terminar de completar.
   */
  const [soloIncompletos, setSoloIncompletos] = useState(false);
  /**
   * «Solo lo que tengo», APAGADO por default.
   *
   * Esta pantalla contesta "que vendo, a cuanto, y de donde sale ese precio".
   * "Que tengo" es la pregunta de /inventario, que ademas la contesta mejor: con el
   * valorizado, los estados y la reposicion.
   *
   * Arrancar filtrado por stock escondia la lista de precios, que es para lo
   * que existe el Catalogo. Y en un negocio que vende sellados A PEDIDO —diez de
   * los once modelos estan en cero— la pantalla abria casi vacia justo cuando
   * alguien llama a preguntar cuanto sale un 17 Pro.
   *
   * Queda como lente, no como regla: prenderlo responde "de este catalogo, que
   * tengo en la mano" sin salir de aca.
   */
  const [soloConStock, setSoloConStock] = useState(false);
  // "Producto nuevo" abre el wizard, que pregunta QUÉ se carga. El formulario
  // completo (DialogProducto) queda para la edición y para la opción "Otro".
  const [dialogoAlta, setDialogoAlta] = useState(false);
  const [dialogo, setDialogo] = useState<"cerrado" | "alta" | "edicion">("cerrado");
  const [dialogoUsado, setDialogoUsado] = useState<"cerrado" | "edicion" | "recepcion">("cerrado");
  const [dialogoExportar, setDialogoExportar] = useState(false);
  const [enEdicion, setEnEdicion] = useState<Producto | null>(null);

  const filtrosServidor = useMemo(
    // El catalogo comun excluye las unidades usadas: viven en su propia vista.
    () => ({ ...filtrosParaBackend(filtros), es_usado: false, limite: TOPE }),
    [filtros],
  );

  /**
   * Los usados van en su propia consulta —son la otra mitad del catalogo— pero
   * con los MISMOS filtros de servidor que el resto. Antes se traian crudos, y
   * eso dejaba a «Dados de baja» y a «Publicados» sin efecto en la unica
   * solapa donde se cargan fichas a mano.
   */
  const filtrosUsados = useMemo(
    () => ({ ...filtrosParaBackend(filtros), es_usado: true, limite: TOPE }),
    [filtros],
  );

  const { data: productos, isFetching } = useProductos(filtrosServidor);
  const { data: usados, isFetching: cargandoUsados } = useProductos(filtrosUsados);
  /**
   * El chequeo de IMEI repetido al editar o recibir un usado necesita TODAS las fichas
   * activas, no las que dejaron los filtros de la pantalla (activo/publicado,
   * en `filtrosUsados`): con «Dados de baja» elegido, `usados` deja afuera
   * justo las fichas activas contra las que hay que comparar, y un IMEI
   * repetido pasaba sin avisar. Misma consulta que Inventario.
   */
  const { data: usadosActivos } = useProductos({
    es_usado: true,
    activo: true,
    limite: TOPE_FILAS,
  });
  const { data: enTallerPagina } = useUsadosEnTaller();
  const enTaller = useMemo(() => indexarEnTaller(enTallerPagina?.filas ?? []), [enTallerPagina]);
  const { data: rubros } = useRubros();
  const { data: proveedores } = useProveedores();
  const { data: config } = useConfig();

  const cotizacion = aNumero(config?.cotizacion_vigente);

  /**
   * Los modelos que este local NO trabaja, LOS DE ESTA SECCION.
   *
   * Vive en la base y no en el navegador: es una decision del negocio, no una
   * preferencia de quien mira. El que atiende en el mostrador y el que carga
   * desde el celular tienen que ver la misma lista.
   *
   * Sellados y usados no comparten la marca: se guardan en la misma columna
   * pero con la clave de su seccion (ver `claveOculta`), y aca se lee solo la
   * mitad que le toca a la solapa abierta.
   */
  const seccionDeModelos: SeccionDeModelos = vista === "usados" ? "usados" : "sellados";
  const ocultos = useMemo(
    () => modelosOcultosDe(config?.config?.modelos_ocultos ?? [], seccionDeModelos),
    [config, seccionDeModelos],
  );
  const guardarConfig = useActualizarConfig();

  function alternarOculto(modelo: string): void {
    const clave = claveOculta(seccionDeModelos, modelo);
    const actuales = config?.config?.modelos_ocultos ?? [];
    const modelos_ocultos = actuales.includes(clave)
      ? actuales.filter((m) => m !== clave)
      : [...actuales, clave];
    guardarConfig.mutate({ modelos_ocultos }, { onError: mostrarError });
  }

  /**
   * Las memorias que no se trabajan. Misma idea un nivel mas abajo: el lineup
   * del desplegable trae TODAS las que Apple hizo, y de un modelo que solo se
   * pide en 256 y 512 sobran dos renglones permanentes.
   */
  const memoriasOcultas = useMemo(
    () => memoriasOcultasDe(config?.config?.memorias_ocultas ?? [], seccionDeModelos),
    [config, seccionDeModelos],
  );

  function alternarMemoriaOculta(memoria: string): void {
    const clave = claveMemoriaOculta(seccionDeModelos, memoria);
    const actuales = config?.config?.memorias_ocultas ?? [];
    const memorias_ocultas = actuales.includes(clave)
      ? actuales.filter((m) => m !== clave)
      : [...actuales, clave];
    guardarConfig.mutate({ memorias_ocultas }, { onError: mostrarError });
  }
  const rubroUsadosId = useMemo(
    () => rubros?.filas.find((r) => r.nombre === "Usados")?.id ?? null,
    [rubros],
  );
  const vistaPorRubro = useMemo(() => mapaRubroASeccion(rubros?.filas ?? []), [rubros]);

  function seccionDe(p: Producto): Vista {
    return seccionDeProducto(p, vistaPorRubro);
  }

  /**
   * Los usados que siguen siendo catalogo.
   *
   * UN EQUIPO VENDIDO NO SE VENDE. Esta pantalla contesta "que vendo y a
   * cuanto", y un usado que ya se fue no es ninguna de las dos cosas: ocupa
   * una fila, infla el contador de la solapa y aparece atenuado entre los que
   * si se pueden ofrecer.
   *
   * No se borra ni se da de baja: la ficha sigue viva —la venta, el remito y
   * el historial de movimientos la necesitan, y el IMEI sigue siendo la prueba
   * de que ese equipo paso por el local—. Lo que deja de hacer es figurar en
   * el catalogo, que es otra cosa.
   */
  const usadosVigentes = useMemo(
    () => (usados?.filas ?? []).filter((p) => !estaVendido(p)),
    [usados],
  );

  /**
   * «Usados» es la solapa de los iPHONE usados, asi que la lista se parte.
   *
   * La solapa esta armada sobre el lineup de Apple —una fila por modelo y
   * memoria, los colores que Apple hizo, las unidades colgadas de su renglon— y
   * un Samsung no tiene nada de eso. Cuando alcanzaba con `es_usado`, el unico
   * Samsung usado del local vivia solo ahi, en la seccion que no sabia
   * dibujarlo, y NO aparecia con los otros telefonos, que es donde lo busca el
   * que pregunta por uno.
   *
   * Los que no son iPhone se suman al catalogo comun y caen en la seccion de su
   * familia, como cualquier otro producto. Siguen siendo usados: el chip, la
   * seña de la ficha y el asistente de edicion salen de `es_usado`, no de la
   * solapa.
   */
  const usadosIphone = useMemo(() => usadosVigentes.filter(esUsadoIphone), [usadosVigentes]);
  const usadosDeOtraMarca = useMemo(
    () => usadosVigentes.filter((p) => !esUsadoIphone(p)),
    [usadosVigentes],
  );

  /**
   * La pagina que le toca a la seccion activa. Usados es la unica que no sale
   * del catalogo comun: la consulta que la trae es la otra.
   */
  const pagina = vista === "usados" ? usados : productos;
  // Las dos consultas alimentan a TODAS las secciones desde que un usado que no
  // es iPhone se lista con su familia: si «Otras marcas» no esperara a la de
  // usados, se dibujaria completa y el Samsung apareceria despues, solo.
  const cargando = cargandoUsados || isFetching;

  /**
   * «A pedido» no es una familia: es una marca que puede llevar cualquier cosa.
   *
   * Vivia como las otras siete —un rubro, y a la seccion entraba lo que tuviera
   * ese rubro— y al mismo tiempo existia `a_pedido`, la casilla que el asistente
   * de usados marca cuando el equipo lo tiene el proveedor. Dos mecanismos, el
   * mismo nombre: un usado a pedido salia en «Usados» con un chip que decia «A
   * pedido», y NO salia en la solapa que se llama asi. Quien lo buscaba por el
   * cartel no lo encontraba.
   *
   * Ahora la solapa es lo que siempre dijo ser: todo lo que se consigue y no
   * esta en el local. Los usados a pedido siguen ademas en «Usados» —cuando se
   * mira que usados hay, se quiere ver tambien lo que se puede conseguir— asi
   * que esta seccion no los saca de ahi: los junta aca.
   */
  const aPedido = useMemo(() => {
    const todos = [...(productos?.filas ?? []), ...usadosVigentes];
    return todos.filter((p) => p.a_pedido);
  }, [productos, usadosVigentes]);

  // El texto se filtra aca: el `q` del backend busca solo por nombre y hace
  // falta encontrar tambien por codigo interno y por codigo del proveedor.
  const porTexto = useMemo(() => {
    const fuente =
      vista === "usados"
        ? usadosIphone
        : vista === "apedido"
          ? aPedido
          : // El catalogo comun mas los usados de otras marcas: la consulta que
            // los trae es la otra, pero la seccion en la que se buscan es la de
            // su familia.
            [...(pagina?.filas ?? []), ...usadosDeOtraMarca];
    return filtrarPorTexto(fuente, filtros.texto);
  }, [vista, usadosIphone, usadosDeOtraMarca, aPedido, pagina, filtros.texto]);

  /**
   * Lo que le toca a la seccion activa, SIN los filtros de familia ni de
   * proveedor. Es el conjunto del que salen las OPCIONES de esos dos filtros:
   * ofrecer solo lo que esta aca es lo que garantiza que elegir cualquier opcion
   * devuelva algo.
   */
  const deSeccion = useMemo(
    // Usados y «A pedido» ya vienen armadas: la primera es una consulta propia
    // y la segunda se define por la marca, no por la familia.
    () =>
      vista === "usados" || vista === "apedido"
        ? porTexto
        : porTexto.filter((p) => seccionDe(p) === vista),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [porTexto, vista, vistaPorRubro],
  );

  /**
   * Los dos filtros se cruzan: las familias que se ofrecen son las del proveedor
   * elegido, y los proveedores los de la familia elegida. Asi ninguna
   * combinacion posible deja la lista vacia.
   *
   * La opcion ELEGIDA siempre se ofrece aunque ya no tenga productos: si
   * desapareciera del desplegable no habria manera de sacarla.
   */
  const cumpleFamilia = (p: Producto): boolean =>
    !filtros.rubroId || String(p.rubro_id) === filtros.rubroId;
  const cumpleMarca = (p: Producto): boolean => !filtros.marca || p.marca === filtros.marca;
  const cumpleProveedor = (p: Producto): boolean =>
    !filtros.proveedorId || String(p.proveedor_id) === filtros.proveedorId;

  /**
   * Cada filtro ofrece lo que existe una vez aplicados LOS OTROS DOS, nunca el
   * propio: si se descontara a si mismo, elegir «Audio» dejaria el desplegable
   * de familia con «Audio» como unica opcion y no habria como volver.
   */
  /* eslint-disable react-hooks/exhaustive-deps */
  const paraFamilias = useMemo(
    () => deSeccion.filter((p) => cumpleMarca(p) && cumpleProveedor(p)),
    [deSeccion, filtros.marca, filtros.proveedorId],
  );
  const paraMarcas = useMemo(
    () => deSeccion.filter((p) => cumpleFamilia(p) && cumpleProveedor(p)),
    [deSeccion, filtros.rubroId, filtros.proveedorId],
  );
  const paraProveedores = useMemo(
    () => deSeccion.filter((p) => cumpleFamilia(p) && cumpleMarca(p)),
    [deSeccion, filtros.rubroId, filtros.marca],
  );

  /** Los productos de la seccion activa, ya con los tres filtros aplicados. */
  const visibles = useMemo(
    () => deSeccion.filter((p) => cumpleFamilia(p) && cumpleMarca(p) && cumpleProveedor(p)),
    [deSeccion, filtros.rubroId, filtros.marca, filtros.proveedorId],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  /**
   * Que le falta a una ficha de ESTA seccion. Es la misma funcion que la lista
   * usa para el contador de la fila y para el detalle: una sola definicion de
   * "falta", leida desde los tres lugares que la necesitan.
   */
  const pendientesDe = useMemo<((p: Producto) => string[]) | null>(() => {
    if (vista === "usados") return pendientesUsado;
    if (vista === "iphone") return pendientesDeIphone;
    return null;
  }, [vista]);

  /**
   * El orden con el que se lee cada seccion.
   *
   * Dos de las ocho no se leen alfabeticamente. Los iPhone se piensan por
   * lineup —lo nuevo arriba— y ordenados por nombre quedarian con el 11 antes
   * del 17 Pro, que es al reves de como se pregunta. Los usados se piensan por
   * disponibilidad: lo que tengo, lo que consigo, lo que ya se vendio. El
   * resto del catalogo llega del servidor en el orden que corresponde.
   */
  const ordenados = useMemo(() => {
    if (vista === "iphone") return ordenarIphones(visibles);
    if (vista === "usados") return ordenarUsados(visibles);
    return visibles;
  }, [vista, visibles]);

  /**
   * Cada unidad, colgada de su modelo.
   *
   * Las dos secciones que tienen equipos tienen la misma estructura: una fila
   * que es EL MODELO —cuanto sale, en que colores viene, y por eso en cero— y
   * las fichas de los equipos concretos. Lo unico que cambia es como se
   * reconoce el modelo: en sellados es el que no tiene color; en usados, el
   * precio de referencia.
   *
   * Listadas al mismo nivel, cada modelo aparecia dos, tres o cuatro veces con
   * nombres casi iguales. Colgadas, el modelo es la entrada y adentro esta lo
   * que hay.
   */
  const porModelo = useMemo(() => {
    if (vista === "iphone") return agruparEnModelos(ordenados, (p) => !p.color);
    if (vista === "usados") return agruparEnModelos(ordenados, esReferencia);
    return { filas: ordenados, unidades: new Map<number, Producto[]>() };
  }, [vista, ordenados]);

  /**
   * Las filas que se muestran. El filtro de incompletos va DESPUES de agrupar:
   * si se aplicara antes, esconderia el modelo de una unidad incompleta y la
   * unidad se quedaria sin donde colgar.
   */
  const agrupa = vista === "iphone" || vista === "usados";

  const filas = useMemo(() => {
    let base = porModelo.filas;
    if (agrupa && soloConStock)
      base = base.filter((p) => stockConUnidades(p, porModelo.unidades) > 0);
    if (soloIncompletos && pendientesDe) {
      /**
       * Un modelo se queda si LE FALTA ALGO A EL O A ALGUNO DE SUS EQUIPOS.
       *
       * Sin la segunda mitad, el cartel decia "2 equipos tienen datos sin
       * completar", se apretaba «Ver solo esos» y no quedaba ninguna fila: los
       * dos equipos estaban colgados adentro de un modelo, y el filtro solo
       * miraba las filas de arriba. Un aviso que no lleva a ningun lado es
       * peor que no tener aviso.
       */
      base = base.filter(
        (p) =>
          pendientesDe(p).length > 0 ||
          (porModelo.unidades.get(p.id) ?? []).some((u) => pendientesDe(u).length > 0),
      );
    }
    return base;
  }, [porModelo, agrupa, soloConStock, soloIncompletos, pendientesDe]);

  /**
   * Las fichas que hay en los grupos: las de los modelos y las que cuelgan de
   * ellos. Es lo que cuenta la solapa («Sellados 13»), mientras que arriba de
   * la tabla se cuentan modelos («9 modelos»): sin nombrar las dos unidades,
   * los dos números parecían contradecirse.
   */
  const fichasDeLosGrupos = agrupa
    ? filas.reduce((t, p) => t + 1 + (porModelo.unidades.get(p.id)?.length ?? 0), 0)
    : undefined;

  /**
   * Lo que la seccion Usados le agrega a cada fila: la seña del equipo, sus
   * chips y «ya lo tengo». Memoizado porque es un objeto: nuevo en cada render
   * seria una prop distinta en cada render.
   */
  const extras = useMemo(
    () =>
      vista === "usados"
        ? extrasDeUsados({
            enTaller,
            onRecibir: abrirRecepcion,
            unidades: porModelo.unidades,
            onEditar: abrirEdicion,
          })
        : vista === "iphone"
          ? extrasDeIphones({ unidades: porModelo.unidades, onEditar: abrirEdicion })
          : undefined,
    // `abrirRecepcion` se re-crea en cada render; lo que importa es la vista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vista, enTaller, porModelo],
  );

  /**
   * Las filas de iPhone, agrupadas por modelo.
   *
   * Un modelo con tres memorias eran tres filas con el nombre casi igual. La
   * seccion arma los grupos —sabe que es una memoria y como se llama un
   * modelo— y la lista solo los dibuja.
   *
   * Usados usa el MISMO armado: sus precios de referencia tambien son uno por
   * modelo y memoria, y lo que cuelga de cada uno son los equipos. Lo unico
   * que cambia es con que boton se carga el que falta.
   */
  /*
   * El lineup entero (los modelos y memorias que no están cargados) solo se
   * agrega cuando se mira el catálogo sin buscar nada. Antes el texto no
   * contaba: «zzzz» o un IMEI dejaban los 9 modelos con sus rayas, y la
   * búsqueda parecía no hacer nada. Con texto quedan los modelos que tienen
   * algo que coincide, y si no queda ninguno, el «No encontré» de siempre.
   */
  const conLineup = mostrarLineup({ soloConStock, soloIncompletos, texto: filtros.texto });

  const grupos = useMemo(
    () =>
      vista === "iphone"
        ? gruposDeModelos({
            filas,
            unidades: porModelo.unidades,
            onEditar: abrirEdicion,
            conLineup,
            comoSeCarga: "Se carga con «Cargar iPhone», eligiendo el modelo y la memoria.",
            extras,
            ocultos,
            onAlternarOculto: alternarOculto,
            memoriasOcultas,
            onAlternarMemoriaOculta: alternarMemoriaOculta,
            onGuardarPrecios: guardarPreciosDeMemoria,
            cotizacion,
            esModelo: (p) => !p.color,
          })
        : vista === "usados"
          ? gruposDeModelos({
              filas,
              unidades: porModelo.unidades,
              onEditar: abrirEdicion,
              conLineup,
              comoSeCarga:
                "No hay precio de referencia para este modelo. Se carga con «Producto nuevo», o solo cuando entre el primer equipo.",
              extras,
              ocultos,
              onAlternarOculto: alternarOculto,
              memoriasOcultas,
              onAlternarMemoriaOculta: alternarMemoriaOculta,
              onGuardarPrecios: guardarPreciosDeMemoria,
              cotizacion,
              esModelo: esReferencia,
            })
          : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vista, filas, porModelo, conLineup, extras, ocultos, memoriasOcultas, cotizacion],
  );

  /**
   * Cuantas fichas de la seccion tienen algo sin completar.
   *
   * Se cuenta sobre `visibles` y no sobre lo que se muestra: con el filtro
   * puesto, lo que se muestra son justamente esas, y el cartel diria "N de N"
   * para siempre. Tambien se cuenta ANTES de colgar las unidades de su modelo:
   * una unidad incompleta adentro de un desplegable sigue estando incompleta.
   */
  const incompletos = useMemo(
    () => (pendientesDe ? visibles.filter((p) => pendientesDe(p).length > 0).length : 0),
    [visibles, pendientesDe],
  );

  /**
   * Cuantos hay en cada seccion, para las solapas. Se cuenta sobre todo lo que
   * trajo el servidor y no sobre lo filtrado: el numero de la solapa «Perfumes»
   * no puede depender de que familia se eligio estando en Accesorios.
   */
  const conteos = useMemo(
    () =>
      conteosDeSecciones(
        [...(productos?.filas ?? []), ...usadosDeOtraMarca],
        usadosIphone,
        aPedido,
        vistaPorRubro,
      ),
    [productos, usadosIphone, usadosDeOtraMarca, aPedido, vistaPorRubro],
  );

  /**
   * Lo que exporta el botón Exportar: exactamente lo que la sección activa
   * muestra, en el orden en que lo muestra. Desde que las nueve secciones son
   * la misma lista no hay nada que recalcular — antes iPhone y Usados
   * filtraban adentro de su componente y acá había que repetir ese criterio.
   */
  const paraExportar: Producto[] = ordenados;

  /**
   * Los rubros que tienen sentido como filtro adentro de la seccion activa: los
   * que caen en ella segun el mismo mapa que arma las secciones, para que
   * elegir uno nunca pueda dejar la lista vacia.
   *
   * «Otras marcas» es la que mas lo necesita: no esta en `RUBROS_POR_VISTA`
   * —es el cajon de lo que no entra en ningun lado— y antes ofrecia los catorce
   * rubros del sistema, Apple y Perfumes incluidos, que ahi no tienen un solo
   * producto. Usados queda afuera aparte: su rubro existe pero vive en su
   * propia solapa, con una ficha por unidad.
   */
  const rubrosDeVista = useMemo(() => {
    const presentes = new Set(paraFamilias.map((p) => p.rubro_id));
    return (rubros?.filas ?? []).filter(
      (r) => presentes.has(r.id) || String(r.id) === filtros.rubroId,
    );
  }, [rubros, paraFamilias, filtros.rubroId]);

  /**
   * Todas las marcas ya usadas en el catalogo, para sugerirlas al cargar. Es
   * distinto de `marcasDeVista`: aquellas son las de la seccion y alimentan el
   * filtro; estas son el vocabulario entero, para que nadie escriba "Sansung".
   */
  const marcasConocidas = useMemo(
    () =>
      [
        ...new Set(
          (productos?.filas ?? []).map((p) => p.marca).filter((m): m is string => Boolean(m)),
        ),
      ].sort((a, b) => a.localeCompare(b, "es")),
    [productos],
  );

  /** Las marcas que existen en la seccion. Los productos sin marca no aportan. */
  const marcasDeVista = useMemo(() => {
    const presentes = new Set(
      paraMarcas.map((p) => p.marca).filter((m): m is string => Boolean(m)),
    );
    if (filtros.marca) presentes.add(filtros.marca);
    return [...presentes].sort((a, b) => a.localeCompare(b, "es"));
  }, [paraMarcas, filtros.marca]);

  /** Los proveedores que tienen algo en esta seccion, con lo demas ya aplicado. */
  const proveedoresDeVista = useMemo(() => {
    const presentes = new Set(paraProveedores.map((p) => p.proveedor_id));
    return (proveedores?.filas ?? []).filter(
      (pr) => presentes.has(pr.id) || String(pr.id) === filtros.proveedorId,
    );
  }, [proveedores, paraProveedores, filtros.proveedorId]);

  /** El producto que se esta contando, fuera de `enEdicion`: la ficha ya se cerro. */
  const [contando, setContando] = useState<Producto | null>(null);
  /**
   * El producto que se esta por borrar.
   *
   * Vive aparte de `enEdicion` y la ficha se cierra al abrirlo, por lo mismo
   * que el conteo: un dialogo adentro de otro es una trampa de foco en el
   * telefono. Ademas, si el borrado sale bien no hay ficha a la que volver.
   */
  const [borrando, setBorrando] = useState<Producto | null>(null);
  /** El producto cuya linea de tiempo se mira. La ficha ya se cerro, igual que al contar. */
  const [viendoMovimientos, setViendoMovimientos] = useState<Producto | null>(null);

  const crear = useCrearProducto();
  const creacion = useCrearGuiado(rubros?.filas ?? [], cotizacion);
  const actualizar = useActualizarProducto();
  const eliminar = useEliminarProducto();
  const ingresar = useIngresoStock();
  const repartido = useIngresoRepartido();
  const salir = useSalidaStock();

  function cambiarVista(v: Vista): void {
    setVista(v);
    // Un filtro elegido en una seccion no debe perseguir a la siguiente.
    setFiltros(FILTROS_INICIALES);
    setSoloIncompletos(false);
    setSoloConStock(false);
  }

  /**
   * El conteo se abre con la ficha cerrada y el producto guardado aparte:
   * `cerrar()` limpia `enEdicion`, y un dialogo adentro de otro dialogo es una
   * trampa de foco en el telefono, que es donde se cuenta.
   */
  function abrirConteo(p: Producto): void {
    cerrar();
    setContando(p);
  }

  function guardarConteo(m: MovimientoDeConteo): void {
    const nombre = contando?.nombre;
    const listo = (r: ResultadoMovimientoStock): void => {
      setContando(null);
      mostrarExito(
        `${nombre ?? "El producto"} quedó en ${mostrarCantidad(aNumero(r.stock_actual))} u.`,
        "Se registró como ajuste de inventario. El costo y el precio quedaron como estaban.",
      );
    };
    /*
     * Aparecieron equipos y se dijo de que color: cada uno va a su ficha.
     *
     * Esta pantalla es la que muestra el cartel de «datos sin completar», asi
     * que es la peor de las dos para que contar deje unidades sin color: se
     * apagaba el cartel contando y volvia a prenderse por el mismo conteo.
     */
    if (m.sentido === "ingreso" && m.reparto && m.reparto.length > 0) {
      void repartirDelConteo(m.datos, m.reparto, nombre);
      return;
    }
    if (m.sentido === "ingreso")
      ingresar.mutate(m.datos, { onSuccess: listo, onError: mostrarError });
    else salir.mutate(m.datos, { onSuccess: listo, onError: mostrarError });
  }

  async function repartirDelConteo(
    datos: IngresoStock,
    reparto: ReadonlyArray<UnidadesDeColor>,
    nombre?: string,
  ): Promise<void> {
    try {
      /*
       * `pagina.filas` y NO `filas`: la segunda es la lista agrupada, donde las
       * fichas de color no estan —cuelgan de su modelo en `porModelo.unidades`—.
       * Buscar ahi no encontraria la ficha que ya existe y crearia una segunda,
       * que es justo el bug que esto vino a sacar.
       */
      const { hechos } = await repartido.ingresar(datos, reparto, pagina?.filas ?? []);
      setContando(null);
      mostrarExito(
        `${nombre ?? "El modelo"} quedó repartido`,
        `${hechos.join(" · ")}. Se registró como ajuste de inventario.`,
      );
    } catch (error) {
      mostrarError(error);
    }
  }

  /**
   * Borrar se pide desde la ficha y se confirma afuera: la ficha se cierra y
   * el producto pasa a `borrando`. Mismo motivo que el conteo —un dialogo
   * adentro de otro atrapa el foco en el telefono— con uno propio: si el
   * borrado sale bien, la ficha que lo abrio ya no tiene producto que mostrar.
   */
  function abrirBorrado(p: Producto): void {
    cerrar();
    setBorrando(p);
  }

  /**
   * Borra de verdad. NO muestra el error: el rechazo esperable —tiene ventas,
   * o una reparacion encima— lo muestra el dialog, que ahi mismo ofrece
   * desactivar. Por eso re-lanza en vez de tragarse la excepcion.
   */
  async function borrarProducto(p: Producto): Promise<void> {
    await eliminar.mutateAsync(p.id);
    setBorrando(null);
    mostrarExito(`${p.nombre} borrado.`, "Se fue del catálogo con sus movimientos de stock.");
  }

  /** El plan B cuando el borrado no se puede: la ficha queda, deja de aparecer. */
  async function desactivarProducto(p: Producto): Promise<void> {
    try {
      await actualizar.mutateAsync({ id: p.id, cambios: { activo: false } });
      setBorrando(null);
      mostrarExito(`${p.nombre} desactivado.`, "No se vende ni aparece en las listas.");
    } catch (error) {
      mostrarError(error);
      throw error;
    }
  }

  function abrirEdicion(p: Producto): void {
    setEnEdicion(p);
    // Una unidad usada se edita con su asistente (que exige la ficha). Un
    // precio de referencia no tiene ficha: va al formulario clásico.
    if (p.es_usado && !esReferencia(p)) setDialogoUsado("edicion");
    else setDialogo("edicion");
  }

  /**
   * Llegar desde el buscador global: `/catalogo-interno?id=123` abre la ficha de ese
   * producto y deja la seccion correcta de fondo.
   *
   * El id se saca de la URL apenas se usa. Si quedara puesto, cerrar la ficha la
   * volveria a abrir en el siguiente render, y no habria forma de salir.
   *
   * Se espera a que las dos listas esten cargadas antes de dar por perdido el
   * producto: la comun y la de usados llegan en consultas distintas.
   */
  const yaAbierto = useRef<number | null>(null);
  useEffect(() => {
    if (idEnlazado === undefined) return;
    if (yaAbierto.current === idEnlazado) return;
    if (!productos || !usados) return;

    const p = [...productos.filas, ...usados.filas].find((x) => x.id === idEnlazado);
    yaAbierto.current = idEnlazado;
    void navegar({ to: "/catalogo-interno", search: {}, replace: true });

    if (!p) {
      mostrarError("Ese producto ya no está en el catálogo.");
      return;
    }
    setVista(seccionDe(p));
    abrirEdicion(p);
    // `abrirEdicion` y `seccionDe` se re-crean en cada render y meterlas en las
    // dependencias volveria a disparar el efecto sin que cambie nada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEnlazado, productos, usados, navegar]);

  /** «Ya lo tengo»: el equipo del proveedor llegó y pasa a ser del local. */
  function abrirRecepcion(p: Producto): void {
    setEnEdicion(p);
    setDialogoUsado("recepcion");
  }

  function cerrar(): void {
    setDialogoAlta(false);
    setDialogo("cerrado");
    setDialogoUsado("cerrado");
    setEnEdicion(null);
  }

  /** El wizard terminó: crear la ficha y, si hay unidades en mano, ingresarlas. */
  async function crearGuiado(tipo: TipoAlta, form: FormAlta): Promise<void> {
    if (await creacion.crear(tipo, form)) cerrar();
  }

  /**
   * La plata de una memoria, cargada desde el renglon.
   *
   * ES LA UNICA PUERTA QUE CREA UNA FICHA SIN QUE ENTRE NADA. El resto del
   * catalogo de equipos se carga cuando entra un aparato —«Cargar iPhone», un
   * remito de compra— y la ficha aparece como consecuencia. Aca no: lo que se
   * esta anotando es CUANTO SALE el 512 del 17 Pro Max, que es una decision de
   * lista de precios y pasa antes de que exista un solo equipo. Justamente por
   * eso la ficha nace sin color y en cero: es un renglon de precios, no un
   * telefono que este en el local.
   *
   * Si la ficha ya existia solo se le corrige la plata. Nada de lo demas se
   * toca: el nombre, la familia y el stock se editan en la ficha, no acá. El
   * precio escrito se guarda como margen, no congelado (`precioDelRenglon`).
   */
  async function guardarPreciosDeMemoria(
    renglon: RenglonDeMemoria,
    precios: PreciosDeMemoria,
  ): Promise<void> {
    const ficha = renglon.fichas[0] ?? null;
    try {
      if (ficha) {
        await actualizar.mutateAsync({
          id: ficha.id,
          cambios: cambiosDeMemoria(precios, ficha, cotizacion),
        });
        mostrarExito(`${ficha.nombre} actualizado`);
        return;
      }
      const esUsado = vista === "usados";
      const nuevo = fichaDeMemoria({
        modelo: renglon.modelo,
        memoriaGb: renglon.memoriaGb,
        esUsado,
        hermana: renglon.hermana,
        precios,
        // Visible en el lineup = publicado (0082): se trabaja si ni la memoria
        // ni el modelo estan marcados como «no lo trabajo».
        oculta: renglon.oculta || ocultos.has(renglon.modelo),
        cotizacion,
      });
      // Sin hermana de quien copiarla, la familia la pone la seccion: es la
      // unica que sabe en cual esta parada, y una ficha sin familia no cae en
      // ninguna solapa —quedaria cargada y sin embargo invisible—.
      if (nuevo.rubro_id === null && esUsado) nuevo.rubro_id = rubroUsadosId;
      const creado = await crear.mutateAsync(nuevo);
      mostrarExito(`${creado.nombre} quedó cargado`, `Código ${creado.codigo_interno}`);
    } catch (error) {
      mostrarError(error);
    }
  }

  /**
   * Llegó un equipo que estaba a pedido: deja de ser del proveedor y pasa a
   * ser stock del local. La ficha no se duplica — cambia de estado y recién
   * ahí recibe su IMEI, que es cuando por fin se lo puede leer.
   */
  async function recibirUsado(form: FormUsado): Promise<void> {
    if (!enEdicion) return;
    try {
      const cambios = aCambiosUsado(form, enEdicion);
      if (Object.keys(cambios).length > 0) {
        await actualizar.mutateAsync({ id: enEdicion.id, cambios });
      }
      await ingresar.mutateAsync({
        producto_id: enEdicion.id,
        cantidad: 1,
        tipo: "COMPRA",
        actualiza_costo: false,
        referencia: "RECEPCION A PEDIDO",
      });
      cerrar();
      mostrarExito(
        `${enEdicion.nombre} entró al stock`,
        "Ya se puede vender como equipo del local.",
      );
    } catch (error) {
      mostrarError(error);
    }
  }

  function guardarUsado(form: FormUsado): void {
    if (dialogoUsado === "recepcion") {
      void recibirUsado(form);
      return;
    }
    if (!enEdicion) return;
    const cambios = aCambiosUsado(form, enEdicion);
    if (Object.keys(cambios).length === 0) {
      cerrar();
      return;
    }
    actualizar.mutate(
      { id: enEdicion.id, cambios },
      {
        onSuccess: () => {
          cerrar();
          mostrarExito("Ficha actualizada");
        },
        onError: mostrarError,
      },
    );
  }

  return (
    <Pagina>
      <PageHeader
        titulo="Catálogo"
        icono={Package}
        acciones={
          <>
            <Button
              type="button"
              variant="outline"
              disabled={paraExportar.length === 0}
              onClick={() => setDialogoExportar(true)}
            >
              <Download aria-hidden="true" />
              Exportar
            </Button>
            {seccionDeEquipos ? null : (
              <>
                <Button
                  type="button"
                  onClick={() => {
                    setEnEdicion(null);
                    setDialogoAlta(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  {ALTA_POR_VISTA[vista].label}
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Las secciones del negocio, con cuantos productos hay en cada una. */}
      <Segmented
        apariencia="chips"
        tamano="sm"
        etiqueta="Sección de productos"
        opciones={VISTAS.map((v) => ({
          valor: v.clave,
          etiqueta: v.label,
          contador: conteos[v.clave],
        }))}
        valor={vista}
        onChange={cambiarVista}
      />

      <ListaProductos
        productos={filas}
        rubros={rubrosDeVista}
        marcas={marcasDeVista}
        proveedores={proveedoresDeVista}
        filtros={filtros}
        onFiltros={setFiltros}
        onEditar={abrirEdicion}
        cargando={cargando}
        totalCargados={filas.length}
        extras={extras}
        grupos={grupos}
        fichasDeLosGrupos={fichasDeLosGrupos}
        vacio={VACIO_POR_VISTA[vista]}
        controles={
          agrupa ? (
            <Segmented
              apariencia="segmentado"
              tamano="sm"
              etiqueta="Qué se lista"
              // «Todos» primero porque es el default y es el trabajo de la
              // pantalla: el catalogo entero, con precio, haya o no unidades.
              opciones={[
                { valor: "todo", etiqueta: "Todos" },
                { valor: "stock", etiqueta: "Con stock" },
              ]}
              valor={soloConStock ? "stock" : "todo"}
              onChange={(v) => setSoloConStock(v === "stock")}
            />
          ) : undefined
        }
        aviso={
          /* El saneamiento del catalogo no se hace solo: hay que poder ver de
             una cuanto falta y llegar a eso con un clic. El numero sale de las
             mismas fichas, asi que baja a medida que se completan y el cartel
             desaparece cuando ya no queda nada. */
          incompletos > 0 || soloIncompletos ? (
            <Aviso
              tono="warning"
              compacto
              accion={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSoloIncompletos((v) => !v)}
                >
                  {soloIncompletos ? "Ver todos" : "Ver solo esos"}
                </Button>
              }
            >
              {incompletos === 1
                ? "1 equipo tiene datos sin completar."
                : `${incompletos} equipos tienen datos sin completar.`}{" "}
              {PORQUE_IMPORTA[vista]}
            </Aviso>
          ) : null
        }
        pie={
          <PieDeLista
            visibles={pagina?.filas.length ?? 0}
            total={pagina?.total ?? 0}
            sustantivo={vista === "usados" ? "equipos usados" : "productos del catálogo"}
            detalle="Es el máximo que se trae de una, y las secciones, los contadores y los filtros de arriba se calculan sobre estos."
            cargando={cargando}
          />
        }
      />

      <DialogAlta
        abierto={dialogoAlta}
        onAbiertoChange={(a) => {
          if (!a) setDialogoAlta(false);
        }}
        rubros={rubros?.filas ?? []}
        proveedores={proveedores?.filas ?? []}
        guardando={creacion.guardando}
        cotizacion={cotizacion}
        tipoInicial={ALTA_POR_VISTA[vista].tipo}
        tipos={ALTA_POR_VISTA[vista].tipos}
        // Un usado entra por Inventario → Ingreso → iPhone usado: es un
        // equipo que llega, no un renglón de la lista de precios.
        conUsado={false}
        familiasDelEquipo={RUBROS_POR_SECCION[vista]}
        existentes={productos?.filas ?? []}
        onCrear={(tipo, form) => void crearGuiado(tipo, form)}
        onCompleto={() => {
          setDialogoAlta(false);
          setEnEdicion(null);
          setDialogo("alta");
        }}
      />

      <DialogProducto
        abierto={dialogo !== "cerrado"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        producto={dialogo === "edicion" ? enEdicion : null}
        productos={productos?.filas}
        altaPreset={dialogo === "alta" && vista === "apedido" ? PRESET_A_PEDIDO : undefined}
        rubros={rubros?.filas ?? []}
        marcasConocidas={marcasConocidas}
        proveedores={proveedores?.filas ?? []}
        cotizacion={cotizacion}
        guardando={crear.isPending || actualizar.isPending}
        onIrAStock={() => {
          cerrar();
          void navegar({ to: "/inventario" });
        }}
        onContarStock={() => {
          if (enEdicion) abrirConteo(enEdicion);
        }}
        onVerMovimientos={() => {
          if (!enEdicion) return;
          const p = enEdicion;
          cerrar();
          setViendoMovimientos(p);
        }}
        onBorrar={() => {
          if (enEdicion) abrirBorrado(enEdicion);
        }}
        onCrear={(datos) =>
          crear.mutate(datos, {
            onSuccess: (nuevo) => {
              cerrar();
              mostrarExito(
                `${nuevo.nombre} quedó cargado`,
                `Código ${nuevo.codigo_interno} · se vende en ${nuevo.precio_moneda}`,
              );
            },
            onError: mostrarError,
          })
        }
        onActualizar={(cambios) => {
          if (!enEdicion) return;
          actualizar.mutate(
            { id: enEdicion.id, cambios },
            {
              onSuccess: () => {
                cerrar();
                mostrarExito("Producto actualizado");
              },
              onError: mostrarError,
            },
          );
        }}
      />

      <DialogConteo
        abierto={contando !== null}
        onAbiertoChange={(a) => {
          if (!a) setContando(null);
        }}
        productos={contando ? [contando] : []}
        productoId={contando?.id ?? null}
        productoFijo
        todos={pagina?.filas ?? []}
        guardando={ingresar.isPending || salir.isPending}
        onConfirmar={guardarConteo}
      />

      <DialogMovimientos
        abierto={viendoMovimientos !== null}
        onAbiertoChange={(a) => {
          if (!a) setViendoMovimientos(null);
        }}
        producto={viendoMovimientos}
      />

      <DialogExportar
        abierto={dialogoExportar}
        onAbiertoChange={setDialogoExportar}
        seccion={VISTAS.find((v) => v.clave === vista)?.label ?? "Catálogo"}
        negocio={config?.config?.nombre ?? "Manzana Mordida"}
        productos={paraExportar}
      />

      <DialogUsado
        abierto={dialogoUsado !== "cerrado"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        modo={dialogoUsado === "recepcion" ? "recepcion" : "alta"}
        producto={dialogoUsado === "cerrado" ? null : enEdicion}
        proveedores={proveedores?.filas ?? []}
        cotizacion={cotizacion}
        productos={usadosActivos?.filas ?? []}
        guardando={crear.isPending || actualizar.isPending || ingresar.isPending}
        onGuardar={guardarUsado}
        onBorrar={() => {
          if (enEdicion) abrirBorrado(enEdicion);
        }}
      />

      {/*
          La confirmacion la comparten las dos fichas —la comun y la del
          usado— porque borrar es lo mismo para las dos: el producto se va o
          no se va. Vive en la pantalla y no adentro de cada dialogo para que
          no haya un dialogo abierto arriba de otro.
      */}
      <DialogBorrarProducto
        producto={borrando}
        abierto={borrando !== null}
        onAbiertoChange={(a) => {
          if (!a) setBorrando(null);
        }}
        onEliminar={borrarProducto}
        onDesactivar={desactivarProducto}
      />
    </Pagina>
  );
}
